import { useTranslation } from 'react-i18next';

import type {
  ContentBlockKind,
  ContentBlockScope,
  ContentBlockStatus,
} from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

const STATUS_STYLES: Record<ContentBlockStatus, string> = {
  active: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  draft: 'border-border text-muted-foreground',
};

/** Green for live, outlined for draft. Blocks and sections share the two statuses. */
export function ContentStatusBadge({
  status,
  className,
}: {
  status: ContentBlockStatus;
  className?: string;
}) {
  const { t } = useTranslation('content');
  return (
    <Badge variant="outline" className={cn(STATUS_STYLES[status], className)}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export function BlockKindBadge({
  kind,
  className,
}: {
  kind: ContentBlockKind;
  className?: string;
}) {
  const { t } = useTranslation('content');
  return (
    <Badge variant="secondary" className={className}>
      {t(`kind.${kind}`)}
    </Badge>
  );
}

/** Only shared blocks get a marker; a store's own blocks are the default. */
export function BlockScopeBadge({
  scope,
  className,
}: {
  scope: ContentBlockScope;
  className?: string;
}) {
  const { t } = useTranslation('content');
  if (scope !== 'shared') return null;
  return (
    <Badge variant="outline" className={cn('text-muted-foreground', className)}>
      {t('scope.shared')}
    </Badge>
  );
}
